// src/app/core/http/auth.interceptor.ts
import type { HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { TenantService } from '../tenant/tenant.service';
import { SessionService } from '../auth/session.service';
import { environment } from '../../../environments/environment';
import { ConfigService } from '../config/config.service';

/**
 * AuthInterceptor — adjunta headers de autenticación a todas las peticiones.
 *
 * Headers adjuntados:
 * - Authorization: Basic {token}
 * - Fineract-Platform-TenantId: {tenantId}  ← SIEMPRE desde TenantService
 * - X-Correlation-ID: {correlationId}
 * - Content-Type: application/json
 * - Accept: application/json
 *
 * REGLA: el tenant NUNCA se hardcodea — siempre viene de TenantService.getTenantId().
 * Si no hay sesión activa, el request pasa sin headers (ej: POST /authentication).
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const auth    = inject(AuthService);
  const tenant  = inject(TenantService);
  const session = inject(SessionService);

  // El propio request que carga runtime-config.json (disparado por APP_INITIALIZER)
  // nunca debe llevar el header de tenant: en ese momento ConfigService aún no ha
  // terminado de cargar, y TenantService.getTenantId() lanzaría un error circular
  // ("la configuración no ha sido cargada"). Esto rompía el bootstrap completo para
  // cualquier usuario ya autenticado (sessionStorage con sesión válida) que recargara
  // la página directamente en una ruta protegida (ej: F5 en /dashboard).
  if (req.url === environment.runtimeConfigUrl) {
    return next(req);
  }

  // Si la configuración aún no ha terminado de cargar, no hay tenant ni demás
  // datos disponibles para armar los headers — dejar pasar sin modificar.
  const config = inject(ConfigService);
  if (!config.isLoaded()) {
    return next(req);
  }

  // Si no hay sesión activa, pasar sin modificar (ej: login, i18n)
  if (!auth.isAuthenticated()) {
    return next(req);
  }

  const token         = auth.token();
  const tenantId      = tenant.getTenantId();
  const correlationId = session.correlationId();

  if (!token) {
    return next(req);
  }

  const authReq = req.clone({
    setHeaders: {
      'Authorization':               `Basic ${token}`,
      'Fineract-Platform-TenantId':  tenantId,
      'X-Correlation-ID':            correlationId,
      'Content-Type':                'application/json',
      'Accept':                      'application/json',
    }
  });

  return next(authReq);
};
